const _excluded = ["type", "children", "className", "prefix", "suffix", "disableSuffixPadding", "label", "required", "success", "testId", "inputProps", "size", "menuMaxHeight", "width", "minWidth", "isMoney", "isPercentage", "alwaysShrinkLabel", "variant", "onKeyDown", "onChange", "warning", "preventOutline", "placeholder", "value", "isSignature", "displayValue", "inputRootClass", "helperTextReserveSpace", "textColor", "select", "SelectProps", "collapseHelperText", "showHelperTextOnFocusOnly", "helperText", "disabled", "error"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { forwardRef, useState } from 'react';
import { Collapse, TextField as MuiTextField, FormHelperText } from '@material-ui/core';
import cn from 'classnames';
import { ArrowDownFill } from "../../../icons";
import { preventBadNumberInputKeys, preventBadNumberInputValues } from "../../../input-management";
import { isValidNumberWithPrecisionOf2, isValidPercentageWithPrecisionOf2 } from "../../../utils/money-input";
import { Box } from "../../Layout";
import { useBaseTextFieldStyles } from "./BaseTextField.styles";
import { TextFieldTestIds } from "./BaseTextField.utils";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const BaseTextField = /*#__PURE__*/forwardRef((props, ref) => {
  var _props$InputProps, _props$InputProps2, _props$InputProps3;
  const {
      type,
      children,
      className,
      prefix,
      suffix,
      disableSuffixPadding,
      label,
      required,
      success,
      testId,
      inputProps,
      size = 'medium',
      menuMaxHeight,
      width,
      minWidth,
      isMoney,
      isPercentage,
      alwaysShrinkLabel,
      variant = 'outlined',
      onKeyDown,
      onChange,
      warning,
      preventOutline,
      placeholder,
      value,
      isSignature,
      displayValue,
      inputRootClass,
      helperTextReserveSpace,
      textColor,
      select,
      SelectProps,
      collapseHelperText = true,
      showHelperTextOnFocusOnly = false,
      helperText,
      disabled,
      error
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [isFocused, setIsFocused] = useState(false);
  const effectiveHelperText = showHelperTextOnFocusOnly && !error && !isFocused ? undefined : helperText;
  const styles = useBaseTextFieldStyles({
    menuMaxHeight,
    width,
    minWidth,
    disableSuffixPadding,
    isSignature,
    helperTextReserveSpace,
    textColor
  });
  const actualType = isMoney || isPercentage ? 'number' : type;
  const isNumberType = actualType === 'number';
  const shouldHackSelectWithPlaceholder = select && placeholder;
  const extraSelectProps = shouldHackSelectWithPlaceholder ? {
    displayEmpty: true,
    renderValue: value => {
      if (Array.isArray(value) ? value.length > 0 : value) {
        const selectedChild = React.Children.toArray(children).find(child => /*#__PURE__*/React.isValidElement(child) && child.props.value === value);
        const selectLabel = /*#__PURE__*/React.isValidElement(selectedChild) ? selectedChild.props.label || selectedChild.props.children : value;
        return /*#__PURE__*/_jsx("div", {
          "data-testid": TextFieldTestIds.label(testId),
          className: styles.ellipsis,
          children: displayValue || selectLabel
        });
      } else {
        return /*#__PURE__*/_jsx("div", {
          "data-testid": TextFieldTestIds.placeholder(testId),
          className: styles.selectPlaceholder,
          children: placeholder
        });
      }
    }
  } : {};
  return /*#__PURE__*/_jsxs("div", {
    className: cn(styles.wrapper, className),
    children: [/*#__PURE__*/_jsx(MuiTextField, _objectSpread(_objectSpread({
      innerRef: ref,
      "data-testid": TextFieldTestIds.root(testId)
    }, rest), {}, {
      select: select,
      placeholder: placeholder,
      value: value,
      type: actualType,
      className: cn({
        [styles.success]: success,
        [styles.warning]: warning,
        [styles.noOutline]: preventOutline
      }, styles.root),
      variant: variant,
      label: required && label ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [label, /*#__PURE__*/_jsx("span", {
          className: "sg-tf-asterix",
          children: "*"
        })]
      }) : label,
      onChange: isNumberType ? e => {
        const newValue = e.currentTarget.value;
        if (Number.isNaN(+newValue)) return;
        preventBadNumberInputValues(e);
        if (isMoney && !isValidNumberWithPrecisionOf2(newValue)) return;
        if (isPercentage && !isValidPercentageWithPrecisionOf2(newValue)) return;
        onChange && onChange(e);
      } : onChange,
      onKeyDown: isNumberType ? e => {
        preventBadNumberInputKeys(e, inputProps && inputProps.min != null && inputProps.min >= 0);
        onKeyDown && onKeyDown(e);
      } : undefined,
      InputProps: {
        ref: (_props$InputProps = props.InputProps) === null || _props$InputProps === void 0 ? void 0 : _props$InputProps.ref,
        startAdornment: prefix || ((_props$InputProps2 = props.InputProps) === null || _props$InputProps2 === void 0 ? void 0 : _props$InputProps2.startAdornment),
        endAdornment: (Array.isArray(suffix) ? /*#__PURE__*/_jsx(Box, {
          gap: 2,
          children: suffix.map(s => s)
        }) : suffix) || ((_props$InputProps3 = props.InputProps) === null || _props$InputProps3 === void 0 ? void 0 : _props$InputProps3.endAdornment),
        required,
        classes: {
          root: cn(styles.inputRoot, inputRootClass),
          input: cn(styles.inputInput, styles["inputInput_".concat(size)])
        }
      },
      SelectProps: _objectSpread(_objectSpread({
        ['data-testid']: TextFieldTestIds.Select(),
        IconComponent: ArrowDownFill,
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          getContentAnchorEl: null,
          autoFocus: false,
          classes: {
            paper: styles.menu
          },
          ['data-testid']: TextFieldTestIds.backdrop(testId)
        }
      }, extraSelectProps), SelectProps) // eslint-disable-line @typescript-eslint/no-explicit-any
      ,
      InputLabelProps: {
        ['data-testid']: TextFieldTestIds.label(testId),
        classes: {
          outlined: cn(styles.label, styles["label_".concat(size)])
        },
        shrink: alwaysShrinkLabel || shouldHackSelectWithPlaceholder || undefined
      } // eslint-disable-line @typescript-eslint/no-explicit-any
      ,
      inputProps: _objectSpread(_objectSpread({
        ['data-testid']: TextFieldTestIds.input(testId),
        step: actualType === 'number' ? 'any' : undefined
      }, isNumberType ? {
        inputMode: 'numeric'
      } : {}), {}, {
        onInvalid: e => {
          e.preventDefault();
        },
        onWheel: isNumberType ? e => {
          // Prevent scroll wheel from changing the value in number inputs
          e.currentTarget.blur();
        } : inputProps === null || inputProps === void 0 ? void 0 : inputProps.onWheel
      }, inputProps),
      FormHelperTextProps: {
        ['data-testid']: TextFieldTestIds.helperText(testId),
        classes: {
          contained: cn(styles.helperText, {
            positionFixed: !helperTextReserveSpace
          })
        }
      } // eslint-disable-line @typescript-eslint/no-explicit-any
      ,
      onFocus: e => {
        var _rest$onFocus;
        setIsFocused(true);
        (_rest$onFocus = rest.onFocus) === null || _rest$onFocus === void 0 || _rest$onFocus.call(rest, e);
      },
      onBlur: e => {
        var _rest$onBlur;
        setIsFocused(false);
        (_rest$onBlur = rest.onBlur) === null || _rest$onBlur === void 0 || _rest$onBlur.call(rest, e);
      },
      helperText: collapseHelperText ? undefined : effectiveHelperText,
      error: error,
      disabled: disabled,
      children: children
    })), collapseHelperText && /*#__PURE__*/_jsx(Collapse, {
      in: !!effectiveHelperText,
      unmountOnExit: true,
      className: styles.collapseWrapper,
      children: /*#__PURE__*/_jsx(FormHelperText, {
        "data-testid": TextFieldTestIds.helperText(testId),
        error: error,
        disabled: disabled && !error,
        className: cn(styles.helperText, styles.helperTextCollapse),
        children: effectiveHelperText || '\u00A0'
      })
    })]
  });
});